import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { generateChat, generateImage, getUserCredits } from '../../services';
import {
  Search, MessageSquare, MessageSquarePlus, Mic, Image as ImageIcon,
  Folder, Settings, Share2, Download, Send,
  ChevronDown, ChevronRight, Edit3, Paperclip, MoreVertical, Sun, Moon, Code,
  Palette, Clapperboard, Lock, PanelLeftClose, PanelLeftOpen, X, Bot, Plug
} from 'lucide-react';
import ConversationContextMenu from '../ConversationContextMenu';
import SettingsModal from '../modals/SettingsModal';
import DownloadMenu from '../DownloadMenu';
import Projects from '../Projects/Projects';
import AgentMode from '../Agent/AgentMode';
import ConnectorsSection from '../Connectors/ConnectorsSection';
import MarkdownRenderer from '../MarkdownRenderer';

// Import GemIQ modals
import ImageGeneratorModal from '../modals/ImageGeneratorModal';
import VideoGeneratorModal from '../modals/VideoGeneratorModal';

// Import Canvas
import Canvas from '../Canvas';

// Vault reskin stylesheet (presentation only). All classes scoped under .qxai-vault.
import './vault-dashboard.css';

/**
 * Dashboard Component - Main Chat Interface (Vault reskin)
 *
 * PRESENTATION-ONLY reskin to match design/QXAI-Dashboard.html.
 * Every handler, state var, and ref from the previous implementation is
 * preserved; only the markup + classes changed.
 */
const Dashboard = () => {
  const { currentUser, userHash, signOut } = useAuth();

  // UI State
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme') || localStorage.getItem('qxai-theme');
    if (savedTheme) return savedTheme;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [notification, setNotification] = useState('');
  const [viewMode, setViewMode] = useState('chat'); // 'chat', 'canvas', or 'projects'

  // Modal states for Image and Video generators
  const [showImageGenerator, setShowImageGenerator] = useState(false);
  const [showVideoGenerator, setShowVideoGenerator] = useState(false);

  // Chat State
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Model Selection
  const [selectedModel, setSelectedModel] = useState('llama-3.2-free');
  const [showModelDropdown, setShowModelDropdown] = useState(false);

  // Canvas
  const [canvasContent, setCanvasContent] = useState('');
  const [canvasMode, setCanvasMode] = useState('visual');
  const [showCanvas, setShowCanvas] = useState(false);
  const [canvasMessage, setCanvasMessage] = useState(null);

  // Share/Download
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');

  // Context Menu
  const [contextMenu, setContextMenu] = useState(null);
  const contextMenuRef = useRef(null);

  // Projects
  const [projects, setProjects] = useState([]);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Voice/File
  const [isRecording, setIsRecording] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const dropdownRef = useRef(null);
  const recognitionRef = useRef(null);

  // Credits
  const [credits, setCredits] = useState(100);

  // Decorative: cipher core cycling hex (seal empty-state)
  const [cipher, setCipher] = useState('7AF2');

  const llmModels = [
    { id: 'llama-3.2-free', name: 'Llama 3.2', free: true, description: 'Fast' },
    { id: 'mistral-7b-free', name: 'Mistral 7B', free: true, description: 'Balanced' },
    { id: 'qwen-coder-free', name: 'Qwen Coder', free: true, description: 'Best for code' },
    { id: 'qwen-72b-free', name: 'Qwen 72B', free: true, description: 'Smart' },
    { id: 'hermes-405b-free', name: 'Hermes 405B', free: true, description: 'Most powerful' },
    { id: 'gpt-4', name: 'GPT-4', free: false, credits: 10 },
    { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', free: false, credits: 1 },
    { id: 'claude-3-opus', name: 'Claude 3 Opus', free: false, credits: 10 },
    { id: 'claude-3-sonnet', name: 'Claude 3 Sonnet', free: false, credits: 5 }
  ];

  const imageModels = [
    { id: 'FLUX-Free', name: 'FLUX Schnell', free: true, description: 'Fast image generation' },
    { id: 'SDXL-Free', name: 'SDXL Turbo', free: true, description: 'Quick results' },
    { id: 'HiDream-Free', name: 'HiDream', free: true, description: 'Creative images' },
    { id: 'flux-pro', name: 'FLUX Pro', free: false, credits: 4, description: 'High quality' },
    { id: 'flux-ultra', name: 'FLUX Pro Ultra', free: false, credits: 6, description: 'Professional quality' },
    { id: 'imagen-4-ultra', name: 'Imagen 4 Ultra', free: false, credits: 6, description: 'Highest quality' },
    { id: 'sd3.5', name: 'SD 3.5', free: false, credits: 5, description: 'Advanced generation' }
  ];

  const videoModels = [
    { id: 'bytedance/seedance-1-lite', name: 'Seedance Lite', free: true, description: 'Free video generation' },
    { id: 'kwaivgi/kling-1.6-standard', name: 'Kling 1.6 Standard', free: false, credits: 18, description: 'Standard quality videos' },
    { id: 'kwaivgi/kling-2.1-standard', name: 'Kling 2.1 Standard', free: false, credits: 25, description: 'Improved quality' },
    { id: 'kwaivgi/kling-1.6-pro', name: 'Kling 1.6 Pro', free: false, credits: 28, description: 'Professional quality' },
    { id: 'bytedance/seedance-1-pro', name: 'Seedance 1 Pro', free: false, credits: 38, description: 'Professional ByteDance' },
    { id: 'google/veo-3', name: 'Google Veo 3', free: false, credits: 65, description: 'Highest quality' }
  ];

  const ttsModels = [
    { id: 'elevenlabs-tts', name: 'ElevenLabs TTS', free: false, credits: 5, description: 'Natural voice synthesis' },
    { id: 'openai-tts', name: 'OpenAI TTS', free: false, credits: 3, description: 'High quality speech' },
    { id: 'google-tts', name: 'Google TTS', free: true, description: 'Free text-to-speech' }
  ];

  const videoAnalysisModels = [
    { id: 'qwen2-vl', name: 'Qwen2-VL', free: true, description: 'Video understanding' },
    { id: 'gpt-4-vision', name: 'GPT-4 Vision', free: false, credits: 10, description: 'Advanced video analysis' },
    { id: 'claude-3-vision', name: 'Claude 3 Vision', free: false, credits: 8, description: 'Video comprehension' }
  ];

  const currentModel = [...llmModels, ...imageModels, ...videoModels, ...ttsModels, ...videoAnalysisModels].find(m => m.id === selectedModel) || llmModels[0];

  // Load user credits on mount
  useEffect(() => {
    const loadCredits = async () => {
      if (userHash) {
        try {
          const userCredits = await getUserCredits(userHash);
          setCredits(userCredits !== undefined && userCredits !== null ? userCredits : 100);
        } catch (error) {
          console.error('❌ Dashboard: Failed to load credits:', error);
          setCredits(100); // Fallback to 100 on error
        }
      }
    };
    loadCredits();
  }, [userHash]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load conversations
  useEffect(() => {
    const saved = localStorage.getItem('qxai_conversations');
    if (saved) {
      try {
        setConversations(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load conversations:', error);
      }
    }
  }, []);

  // Load projects
  useEffect(() => {
    const saved = localStorage.getItem('qxai_projects');
    if (saved) {
      try {
        setProjects(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load projects:', error);
      }
    }
  }, []);

  // Save conversations
  useEffect(() => {
    if (conversations.length > 0) {
      localStorage.setItem('qxai_conversations', JSON.stringify(conversations));
    }
  }, [conversations]);

  // Save projects
  useEffect(() => {
    if (projects.length > 0) {
      localStorage.setItem('qxai_projects', JSON.stringify(projects));
    }
  }, [projects]);

  // Sync messages with conversations (only when messages change, not when loading)
  useEffect(() => {
    if (currentConversationId && messages.length > 0) {
      setConversations(prev => {
        const current = prev.find(c => c.id === currentConversationId);
        // Only update if messages actually changed
        if (current && JSON.stringify(current.messages) !== JSON.stringify(messages)) {
          return prev.map(conv =>
            conv.id === currentConversationId
              ? { ...conv, messages: messages }
              : conv
          );
        }
        return prev;
      });
    }
  }, [messages, currentConversationId]);

  // Load messages when switching conversations
  useEffect(() => {
    if (currentConversationId) {
      const conversation = conversations.find(c => c.id === currentConversationId);
      if (conversation && conversation.messages && JSON.stringify(conversation.messages) !== JSON.stringify(messages)) {
        setMessages(conversation.messages);
      }
    }
  }, [currentConversationId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowModelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Decorative cipher core cycling (respect reduced-motion)
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const hex = '0123456789ABCDEF';
    const id = setInterval(() => {
      let s = '';
      for (let i = 0; i < 4; i++) s += hex[Math.floor(Math.random() * 16)];
      setCipher(s);
    }, 420);
    return () => clearInterval(id);
  }, []);

  // Canvas detection function
  const shouldShowCanvas = (message) => {
    if (!message || !message.content) return false;

    // Check for code blocks
    const hasCodeBlock = /```(\w+)?\n([\s\S]*?)```/.test(message.content);

    // Check for HTML
    const hasHTML = message.content.includes('<!DOCTYPE') ||
                    message.content.includes('<html>');

    // Check for React/JSX
    const hasReact = message.content.includes('export default') ||
                     (message.content.includes('function') && message.content.includes('return ('));

    return hasCodeBlock || hasHTML || hasReact;
  };

  // Auto-open Canvas when code appears
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      // Show Canvas for assistant messages with code
      if (lastMessage.role === 'assistant' && shouldShowCanvas(lastMessage)) {
        setCanvasMessage(lastMessage);
        setShowCanvas(true);
      }
    }
  }, [messages]);

  // Apply theme - drives BOTH the existing app mechanism AND the vault skin
  useEffect(() => {
    // Set data-theme on body for backward compatibility
    document.body.setAttribute('data-theme', theme);
    // Persist under both keys (existing app key + design key)
    localStorage.setItem('theme', theme);
    localStorage.setItem('qxai-theme', theme);

    // TAILWIND: Add/remove 'dark' class on documentElement
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Show notification helper
  const showNotification = useCallback((message) => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  }, []);

  // Preprocess markdown to ensure proper formatting
  const preprocessMarkdown = (text) => {
    if (!text) return text;

    let processed = text;

    // Add double line breaks before numbered lists (1. 2. 3. etc.)
    processed = processed.replace(/(\.)\s+(\d+\.\s+)/g, '$1\n\n$2');

    // Add double line breaks after colons followed by numbered lists
    processed = processed.replace(/(\w:)\s*(\d+\.)/g, '$1\n\n$2');

    // Add double line breaks before bullet points that follow text
    processed = processed.replace(/(\.)\s+(\*\s+)/g, '$1\n\n$2');

    // Ensure bold text markers have proper spacing
    processed = processed.replace(/\*\*([^*]+)\*\*:/g, '**$1**:');

    // Add line breaks after colons that precede descriptions
    processed = processed.replace(/:\s+([A-Z])/g, ': $1');

    return processed;
  };

  // Handle message submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || isLoading) return;

    const userMessage = { role: 'user', content: query };
    setMessages(prev => [...prev, userMessage]);

    // Create new conversation if this is the first message
    let conversationId = currentConversationId;
    if (!conversationId && messages.length === 0) {
      conversationId = Date.now().toString();
      const newConversation = {
        id: conversationId,
        title: query.slice(0, 50) + (query.length > 50 ? '...' : ''),
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        starred: false,
        archived: false,
        projectId: null
      };
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversationId(conversationId);
    }

    const currentQuery = query;
    setQuery('');
    setIsLoading(true);

    try {
      // Detect model type by checking model arrays
      const isImageModel = imageModels.some(m => m.id === selectedModel);
      const isVideoModel = videoModels.some(m => m.id === selectedModel);
      const isTTSModel = ttsModels.some(m => m.id === selectedModel);
      const isVideoAnalysisModel = videoAnalysisModels.some(m => m.id === selectedModel);

      if (isVideoModel) {
        // Import generateVideo from apiService
        const { generateVideo } = await import('../../services/apiService');

        const result = await generateVideo({
          prompt: currentQuery,
          model: selectedModel,
          duration: 5,
          aspectRatio: '16:9'
        });

        if (result.success) {
          const assistantMessage = {
            role: 'assistant',
            content: result.video_url,
            type: 'video',
            model: selectedModel
          };
          setMessages(prev => [...prev, assistantMessage]);
          showNotification('Video generated!');
        } else {
          throw new Error(result.error || 'Video generation failed');
        }
      } else if (isImageModel) {
        // Import generateImage from apiService
        const { generateImage } = await import('../../services/apiService');

        const result = await generateImage({
          prompt: currentQuery,
          model: selectedModel,
          width: 1024,
          height: 1024
        });

        if (result.success) {
          const assistantMessage = {
            role: 'assistant',
            content: result.image_url,
            type: 'image',
            model: selectedModel
          };
          setMessages(prev => [...prev, assistantMessage]);
          showNotification('Image generated!');
        } else {
          throw new Error(result.error || 'Image generation failed');
        }
      } else if (isTTSModel) {
        // Import generateAudio from apiService
        const { generateAudio } = await import('../../services/apiService');

        const result = await generateAudio(currentQuery, 'alloy', selectedModel);

        if (result.success || result.audio_url) {
          const assistantMessage = {
            role: 'assistant',
            content: result.audio_url || result.url,
            type: 'audio',
            model: selectedModel
          };
          setMessages(prev => [...prev, assistantMessage]);
          showNotification('Audio generated!');
        } else {
          throw new Error(result.error || 'Audio generation failed');
        }
      } else if (isVideoAnalysisModel) {
        // Video analysis uses chat API with vision models
        const response = await generateChat(userHash, currentQuery, selectedModel);

        let responseText;
        if (typeof response === 'string') {
          responseText = response;
        } else if (response && typeof response === 'object') {
          responseText = response.response || response.content || response.message || response.text;
          if (!responseText) {
            console.warn('Unexpected response format:', response);
            responseText = 'Received unexpected response format. Please try again.';
          }
        } else {
          responseText = String(response || 'No response received');
        }

        responseText = responseText.replace(/\[OUT\]\s*/g, '').replace(/\s*\[\/OUT\]/g, '');
        responseText = preprocessMarkdown(responseText);

        const assistantMessage = {
          role: 'assistant',
          content: responseText,
          model: selectedModel,
          type: 'video-analysis'
        };
        setMessages(prev => [...prev, assistantMessage]);

        setCanvasContent(prev => {
          const newContent = `${prev}\n\nUser: ${currentQuery}\n\nAssistant: ${responseText}`;
          return newContent.trim();
        });
      } else {
        const response = await generateChat(userHash, currentQuery, selectedModel);

        let responseText;
        if (typeof response === 'string') {
          responseText = response;
        } else if (response && typeof response === 'object') {
          responseText = response.response || response.content || response.message || response.text;
          if (!responseText) {
            console.warn('Unexpected response format:', response);
            responseText = 'Received unexpected response format. Please try again.';
          }
        } else {
          responseText = String(response || 'No response received');
        }

        // Strip [OUT] and [/OUT] tags from response
        responseText = responseText.replace(/\[OUT\]\s*/g, '').replace(/\s*\[\/OUT\]/g, '');

        // Preprocess text to ensure proper markdown formatting
        responseText = preprocessMarkdown(responseText);

        const assistantMessage = {
          role: 'assistant',
          content: responseText,
          model: selectedModel
        };
        setMessages(prev => [...prev, assistantMessage]);

        // Update canvas content
        setCanvasContent(prev => {
          const newContent = `${prev}\n\nUser: ${currentQuery}\n\nAssistant: ${responseText}`;
          return newContent.trim();
        });
      }

      // Update conversation with new messages
      if (conversationId) {
        setConversations(prev => prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, updatedAt: new Date().toISOString() }
            : conv
        ));
      }

      // Refresh credits
      try {
        const newCredits = await getUserCredits(userHash);
        setCredits(newCredits);
      } catch (error) {
        console.error('Failed to refresh credits:', error);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      showNotification(error.message || 'Failed to send message');

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '⚠️ ' + (error.message || 'Failed to generate response. Please try again.'),
        error: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    setTimeout(() => {
      document.querySelector('.chat-input')?.focus();
    }, 100);
  };

  // Handle voice input
  const handleVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      showNotification('Speech recognition not supported');
      return;
    }

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsRecording(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setQuery(prev => prev + ' ' + transcript);
      setIsRecording(false);
    };

    recognition.onerror = () => {
      setIsRecording(false);
      showNotification('Speech recognition error');
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognition.start();
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      showNotification(`File uploaded: ${file.name}`);
    }
  };

  // Handle model select
  const handleModelSelect = (modelId) => {
    setSelectedModel(modelId);
    setShowModelDropdown(false);
  };

  // Handle image generate (from modal)
  const handleImageGenerate = async (prompt, settings) => {
    try {
      const result = await generateImage(userHash, prompt, settings.model);
      if (result.success) {
        showNotification('Image generated successfully!');
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: result.image_url,
          type: 'image',
          model: settings.model
        }]);
      }
    } catch (error) {
      showNotification('Image generation failed');
    }
  };

  // Handle video generate (from modal)
  const handleVideoGenerate = async (prompt, settings) => {
    try {
      showNotification('Video generation started...');
      // Implement video generation logic here
    } catch (error) {
      showNotification('Video generation failed');
    }
  };

  // Conversation handlers
  const handleNewChat = () => {
    setMessages([]);
    setCurrentConversationId(null);
    setCanvasContent('');
    setViewMode('chat');
  };

  const handleStarConversation = (id) => {
    setConversations(prev => prev.map(conv =>
      conv.id === id ? { ...conv, starred: !conv.starred } : conv
    ));
  };

  const handleRenameConversation = (id, newTitle) => {
    const newName = newTitle || prompt('Enter new name:');
    if (newName) {
      setConversations(prev => prev.map(conv =>
        conv.id === id ? { ...conv, title: newName } : conv
      ));
    }
  };

  const handleArchiveConversation = (id) => {
    setConversations(prev => prev.map(conv =>
      conv.id === id ? { ...conv, archived: !conv.archived } : conv
    ));
  };

  const handleDeleteConversation = (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Delete this conversation?')) {
      setConversations(prev => prev.filter(conv => conv.id !== id));
    }
  };

  const handleChangeProject = (conversationId, projectId) => {
    setConversations(prev => prev.map(conv =>
      conv.id === conversationId ? { ...conv, projectId } : conv
    ));
  };

  const handleRemoveFromProject = (conversationId) => {
    setConversations(prev => prev.map(conv =>
      conv.id === conversationId ? { ...conv, projectId: null } : conv
    ));
  };

  // Project handlers
  const handleCreateProject = (projectData) => {
    const newProject = {
      id: Date.now().toString(),
      name: projectData.name,
      description: projectData.description || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      starred: false
    };
    setProjects(prev => [newProject, ...prev]);
    showNotification('Project created!');
  };

  const handleUpdateProject = (projectId, updates) => {
    setProjects(prev => prev.map(proj =>
      proj.id === projectId
        ? { ...proj, ...updates, updatedAt: new Date().toISOString() }
        : proj
    ));
    showNotification('Project updated!');
  };

  const handleDeleteProject = (projectId) => {
    setProjects(prev => prev.filter(proj => proj.id !== projectId));
    setConversations(prev => prev.map(conv =>
      conv.projectId === projectId ? { ...conv, projectId: null } : conv
    ));
    showNotification('Project deleted!');
  };

  const handleShare = () => {
    const link = `https://qxai.space/share/${Date.now()}`;
    setShareLink(link);
    setShowShareModal(true);
  };

  const handleDownload = (format) => {
    showNotification(`Downloading as ${format}...`);
    setShowDownloadMenu(false);
  };

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const visibleConversations = conversations
    .filter(c => !c.archived)
    .filter(c => !searchQuery || c.title.toLowerCase().includes(searchQuery.toLowerCase()));

  const avatarLetter = currentUser?.email?.[0]?.toUpperCase() || 'U';

  // -------- Reusable nav (shared by sidebar + mobile drawer) --------
  const renderNav = (onNavigate = () => {}) => (
    <>
      <div className="nav-group">
        <button
          className={`nav-item ${viewMode === 'chat' && messages.length === 0 ? 'active' : ''}`}
          onClick={() => { handleNewChat(); onNavigate(); }}
        >
          <MessageSquarePlus /><span className="label-text">New Chat</span>
        </button>
        <button
          className="nav-item"
          onClick={() => { handleVoiceInput(); onNavigate(); }}
        >
          <Mic style={isRecording ? { color: 'oklch(0.6 0.2 25)' } : undefined} />
          <span className="label-text">Voice</span>
        </button>
        <button
          className="nav-item"
          onClick={() => { setShowImageGenerator(true); onNavigate(); }}
        >
          <ImageIcon /><span className="label-text">Imagine</span>
        </button>
        <button
          className={`nav-item ${viewMode === 'projects' ? 'active' : ''}`}
          onClick={() => { setViewMode('projects'); onNavigate(); }}
        >
          <Folder /><span className="label-text">Projects</span>
          <ChevronRight className="chev" />
        </button>
        <button
          className={`nav-item ${viewMode === 'agent' ? 'active' : ''}`}
          onClick={() => { setViewMode('agent'); onNavigate(); }}
        >
          <Bot /><span className="label-text">Agent</span>
          <ChevronRight className="chev" />
        </button>
        <button
          className={`nav-item ${viewMode === 'connectors' ? 'active' : ''}`}
          onClick={() => { setViewMode('connectors'); onNavigate(); }}
        >
          <Plug /><span className="label-text">Connected Apps</span>
          <ChevronRight className="chev" />
        </button>
      </div>

      <div className="sb-divider" />

      <div className="sb-section-label"><span className="label-text">Recent</span></div>
      <div className="nav-group">
        {visibleConversations.map(conversation => (
          <div
            key={conversation.id}
            className={`history-item ${currentConversationId === conversation.id ? 'active' : ''}`}
            onClick={() => { setViewMode('chat'); setCurrentConversationId(conversation.id); onNavigate(); }}
          >
            <MessageSquare />
            <span className="txt">{conversation.title}</span>
            <button
              className="more"
              onClick={(e) => {
                e.stopPropagation();
                setContextMenu({
                  conversation,
                  position: { x: e.clientX, y: e.clientY }
                });
              }}
              title="More"
              aria-label="More"
            >
              <MoreVertical />
            </button>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="qxai-vault" data-theme={theme}>
      <div className={`app ${isSidebarCollapsed ? 'collapsed' : ''} ${showCanvas ? 'with-canvas' : ''}`}>

        {/* MOBILE MENU BUTTON */}
        <button
          className="mobile-btn"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          <PanelLeftOpen />
        </button>

        {/* MOBILE DRAWER */}
        <div className={`mobile-overlay ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="scrim" onClick={() => setMobileMenuOpen(false)} />
          <aside className="drawer">
            <div className="sb-top">
              <div className="logo"><img className="logo-img" src={theme === 'dark' ? '/qxai_logo_white.png' : '/qxai_black.png'} alt="QXAI" /></div>
              <button className="icon-btn" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
                <X />
              </button>
            </div>
            <div className="search">
              <Search />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="sb-scroll">
              {renderNav(() => setMobileMenuOpen(false))}
            </div>
            <div className="sb-user">
              <div className="avatar">{avatarLetter}</div>
              <div className="who">
                <div className="email">{currentUser?.email || 'User'}</div>
                <div className="credits"><span className="dot" />{credits} credits</div>
              </div>
              <button className="icon-btn" onClick={() => { setShowSettingsModal(true); setMobileMenuOpen(false); }} aria-label="Settings">
                <Settings />
              </button>
            </div>
          </aside>
        </div>

        {/* SIDEBAR */}
        <aside className="sidebar">
          <div className="sb-top">
            <div className="logo"><img className="logo-img" src={theme === 'dark' ? '/qxai_logo_white.png' : '/qxai_black.png'} alt="QXAI" /></div>
            <button
              className="icon-btn"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label="Toggle sidebar"
            >
              {isSidebarCollapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            </button>
          </div>

          <div className="search">
            <Search />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <kbd>⌘K</kbd>
          </div>

          <div className="sb-scroll">
            {renderNav()}
          </div>

          <div className="sb-user">
            <div className="avatar">{avatarLetter}</div>
            <div className="who">
              <div className="email">{currentUser?.email || 'User'}</div>
              <div className="credits"><span className="dot" />{credits} credits</div>
            </div>
            <button
              className="icon-btn"
              onClick={() => setShowSettingsModal(true)}
              title="Settings"
              aria-label="Settings"
            >
              <Settings />
            </button>
          </div>
        </aside>

        {/* MAIN */}
        <main className="main">
          {viewMode === 'projects' ? (
            <Projects
              projects={projects}
              conversations={conversations}
              onClose={() => setViewMode('chat')}
              onCreate={handleCreateProject}
              onUpdate={handleUpdateProject}
              onDelete={handleDeleteProject}
            />
          ) : viewMode === 'agent' ? (
            <AgentMode />
          ) : viewMode === 'connectors' ? (
            <ConnectorsSection />
          ) : (
            <>
              {/* TOPBAR */}
              <header className="topbar">
                <div className="title">
                  Chat
                  <span className="status-chip"><span className="dot" />Private</span>
                </div>
                <div className="spacer" />
                <button
                  className="icon-btn"
                  onClick={toggleTheme}
                  title="Toggle theme"
                  aria-label="Toggle theme"
                  style={{ border: '1px solid var(--rule)', width: 40, height: 40, borderRadius: 2 }}
                >
                  {theme === 'dark' ? <Sun /> : <Moon />}
                </button>
                <button className="tb-btn" onClick={handleShare}>
                  <Share2 /><span className="btn-text">Share</span>
                </button>
                <div className="tb-wrap">
                  <button className="tb-btn primary" onClick={() => setShowDownloadMenu(!showDownloadMenu)}>
                    <Download /><span className="btn-text">Download</span>
                  </button>
                  {showDownloadMenu && (
                    <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, zIndex: 60 }}>
                      <DownloadMenu
                        messages={messages}
                        conversationTitle="QXAI Conversation"
                        onClose={() => setShowDownloadMenu(false)}
                      />
                    </div>
                  )}
                </div>
              </header>

              {/* CANVAS / MESSAGES */}
              <section className={`canvas ${messages.length > 0 ? 'has-messages' : ''}`}>
                {messages.length === 0 && (
                  <>
                    <span className="tick tl"><span className="plus" />QXAI · VAULT // 01</span>
                    <span className="tick tr">SHA-256 / AES-256-GCM<span className="plus" /></span>
                    <span className="tick bl"><span className="plus" />HASH-KEYED · 90-DAY EXPIRY</span>
                    <span className="tick br">PRIVACY-FIRST<span className="plus" /></span>
                  </>
                )}

                {messages.length > 0 ? (
                  <div className="msg-list">
                    {messages.map((message, index) => (
                      <div key={index} className={`msg ${message.role === 'user' ? 'user' : 'assistant'}`}>
                        <div className="msg-avatar">
                          {message.role === 'user' ? avatarLetter : 'AI'}
                        </div>
                        <div className={`bubble ${message.error ? 'error' : ''}`}>
                          {message.type === 'image' ? (
                            <img src={message.content} alt="Generated" />
                          ) : message.type === 'video' ? (
                            <video src={message.content} controls />
                          ) : message.type === 'audio' ? (
                            <audio src={message.content} controls />
                          ) : message.role === 'assistant' ? (
                            <MarkdownRenderer content={message.content} />
                          ) : (
                            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{message.content}</p>
                          )}

                          {message.role === 'assistant' && shouldShowCanvas(message) && (
                            <button
                              className="canvas-btn"
                              onClick={() => {
                                setCanvasMessage(message);
                                setShowCanvas(true);
                              }}
                            >
                              <Code />Open in Canvas
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                ) : (
                  // Empty / seal state (decorative seal)
                  <div className="empty">
                    <div className="seal">
                      <svg viewBox="0 0 200 200">
                        <defs>
                          <radialGradient id="qxvSealGlow" cx="0.5" cy="0.5" r="0.5">
                            <stop offset="0" stopColor="var(--accent)" stopOpacity="0.16" />
                            <stop offset="0.65" stopColor="var(--accent)" stopOpacity="0" />
                          </radialGradient>
                        </defs>
                        <circle cx="100" cy="100" r="96" fill="url(#qxvSealGlow)" />
                        <circle
                          className="spin" cx="100" cy="100" r="86" fill="none"
                          stroke="var(--accent)" strokeWidth="1" strokeDasharray="2 9" opacity="0.7"
                          style={{ transformOrigin: '100px 100px', animation: reduceMotion ? 'none' : 'qxv-seal-cw 24s linear infinite' }}
                        />
                        <circle className="spin" cx="100" cy="100" r="66" fill="none" stroke="var(--rule)" strokeWidth="1" />
                        <circle
                          className="spin" cx="100" cy="100" r="66" fill="none"
                          stroke="var(--accent)" strokeWidth="1.5" strokeDasharray="30 180" opacity="0.9"
                          style={{ transformOrigin: '100px 100px', animation: reduceMotion ? 'none' : 'qxv-seal-ccw 14s linear infinite' }}
                        />
                        <circle cx="100" cy="100" r="46" fill="var(--bg-1)" stroke="var(--rule)" strokeWidth="1" />
                        <circle
                          className="spin" cx="100" cy="100" r="46" fill="none"
                          stroke="var(--accent)" strokeWidth="0.75" strokeDasharray="4 6" opacity="0.55"
                          style={{ transformOrigin: '100px 100px', animation: reduceMotion ? 'none' : 'qxv-seal-cw 9s linear infinite' }}
                        />
                        <g stroke="var(--fg)" strokeWidth="1">
                          <line x1="100" y1="6" x2="100" y2="16" /><line x1="100" y1="184" x2="100" y2="194" />
                          <line x1="6" y1="100" x2="16" y2="100" /><line x1="184" y1="100" x2="194" y2="100" />
                        </g>
                        <circle cx="100" cy="100" r="46" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0">
                          {!reduceMotion && <animate attributeName="r" values="46;96" dur="2.6s" repeatCount="indefinite" />}
                          {!reduceMotion && <animate attributeName="opacity" values="0.5;0" dur="2.6s" repeatCount="indefinite" />}
                        </circle>
                        <text x="100" y="105" textAnchor="middle" fontFamily="var(--mono)" fontSize="15" letterSpacing="4" fill="var(--fg)">{cipher}</text>
                        <text x="100" y="78" textAnchor="middle" fontFamily="var(--mono)" fontSize="8" letterSpacing="3" fill="var(--fg-faint)">CIPHER</text>
                        <text x="100" y="128" textAnchor="middle" fontFamily="var(--mono)" fontSize="7" letterSpacing="2" fill="var(--accent)">SEALED</text>
                      </svg>
                    </div>

                    <div className="mark"><img className="mark-img" src={theme === 'dark' ? '/qxai_logo_white.png' : '/qxai_black.png'} alt="QXAI" /></div>
                    <div className="divider" />
                    <div className="tagline">AI Without Surveillance</div>
                    <div className="sub">Privacy-first · conversations expire after 90 days</div>
                  </div>
                )}
              </section>

              {/* COMPOSER */}
              <div className="composer-wrap">
                {/* Model dropdown */}
                {showModelDropdown && (
                  <div className="model-menu" ref={dropdownRef}>
                    {[
                      { label: 'Text Models', list: llmModels },
                      { label: 'Image Models', list: imageModels },
                      { label: 'Video Generation', list: videoModels },
                      { label: 'Text-to-Speech', list: ttsModels },
                      { label: 'Video Analysis', list: videoAnalysisModels }
                    ].map(group => (
                      <div className="model-group" key={group.label}>
                        <h4>{group.label}</h4>
                        {group.list.map(model => (
                          <button
                            key={model.id}
                            className={`model-opt ${selectedModel === model.id ? 'active' : ''}`}
                            onClick={() => handleModelSelect(model.id)}
                          >
                            <span>{model.name}</span>
                            <span className={`tag ${model.free ? 'free' : 'premium'}`}>
                              {model.free ? 'Free' : 'Premium'}
                            </span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                <form className="composer" onSubmit={handleSubmit}>
                  <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} />
                  <button
                    type="button"
                    className="attach"
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach"
                    aria-label="Attach"
                  >
                    <Paperclip />
                  </button>

                  <textarea
                    rows={1}
                    className="field chat-input"
                    placeholder="What do you want to know?"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    disabled={isLoading}
                  />

                  <button
                    type="button"
                    className={`mic ${isRecording ? 'recording' : ''}`}
                    onClick={handleVoiceInput}
                    title="Voice input"
                    aria-label="Voice input"
                  >
                    <Mic />
                  </button>

                  <button
                    type="button"
                    className="model-select"
                    onClick={() => setShowModelDropdown(!showModelDropdown)}
                  >
                    <span className="ph" />{currentModel.name}<ChevronDown />
                  </button>

                  <button
                    type="submit"
                    className="send"
                    disabled={!query.trim() || isLoading}
                    title="Send"
                    aria-label="Send"
                  >
                    <Send />
                  </button>
                </form>

                {/* Chips / Canvas toggle */}
                {messages.length === 0 ? (
                  <div className="chips">
                    <button className="chip" onClick={() => handleSuggestionClick('Explain quantum computing')}>
                      <Search />Deepsearch
                    </button>
                    <button className="chip" onClick={() => setShowImageGenerator(true)}>
                      <Palette />Image Generator
                    </button>
                    <button className="chip" onClick={() => setShowVideoGenerator(true)}>
                      <Clapperboard />Create Video
                    </button>
                  </div>
                ) : (
                  <div className="chips">
                    <button
                      className="chip"
                      onClick={() => {
                        const lastMessage = messages[messages.length - 1];
                        if (lastMessage && lastMessage.role === 'assistant') {
                          setCanvasMessage(lastMessage);
                          setShowCanvas(true);
                        }
                      }}
                    >
                      <Edit3 />Go to Canvas
                    </button>
                  </div>
                )}

                <div className="composer-foot">
                  <Lock />Encrypted in transit · account identified by hash, not email
                </div>
              </div>
            </>
          )}
        </main>

        {/* CANVAS PANEL — split column on desktop, overlay on mobile */}
        {showCanvas && (
          <div className="canvas-col">
            <Canvas message={canvasMessage} onClose={() => setShowCanvas(false)} />
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'oklch(0 0 0 / 0.55)', display: 'grid', placeItems: 'center', zIndex: 100 }}
          onClick={() => setShowShareModal(false)}
        >
          <div
            style={{
              background: 'var(--panel)', border: '1px solid var(--rule)', borderRadius: 4,
              padding: 24, maxWidth: 440, width: '90%', boxShadow: 'var(--shadow)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: 24, margin: '0 0 6px', color: 'var(--fg)' }}>
              Share Conversation
            </h2>
            <p style={{ color: 'var(--fg-dim)', margin: '0 0 16px', fontSize: 14 }}>
              Anyone with this link can view the conversation:
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={shareLink}
                readOnly
                style={{
                  flex: 1, padding: '10px 12px', background: 'var(--bg-1)',
                  border: '1px solid var(--rule)', borderRadius: 2, color: 'var(--fg)',
                  fontFamily: 'var(--mono)', fontSize: 12
                }}
              />
              <button
                className="tb-btn primary"
                onClick={() => {
                  navigator.clipboard.writeText(shareLink);
                  showNotification('Link copied!');
                }}
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <SettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          theme={theme}
          setTheme={setTheme}
          onSignOut={signOut}
        />
      )}

      {/* Image Generator Modal */}
      {showImageGenerator && (
        <ImageGeneratorModal
          isOpen={showImageGenerator}
          onClose={() => setShowImageGenerator(false)}
          onGenerate={handleImageGenerate}
          userHash={userHash}
          credits={credits}
        />
      )}

      {/* Video Generator Modal */}
      {showVideoGenerator && (
        <VideoGeneratorModal
          isOpen={showVideoGenerator}
          onClose={() => setShowVideoGenerator(false)}
          showNotification={showNotification}
          onGenerate={handleVideoGenerate}
          userHash={userHash}
          credits={credits}
        />
      )}

      {/* Context Menu */}
      {contextMenu && (
        <ConversationContextMenu
          ref={contextMenuRef}
          conversation={contextMenu.conversation}
          position={contextMenu.position}
          projects={projects}
          onStar={() => handleStarConversation(contextMenu.conversation.id)}
          onRename={(newTitle) => handleRenameConversation(contextMenu.conversation.id, newTitle)}
          onArchive={() => handleArchiveConversation(contextMenu.conversation.id)}
          onDelete={() => handleDeleteConversation(contextMenu.conversation.id)}
          onChangeProject={(projectId) => handleChangeProject(contextMenu.conversation.id, projectId)}
          onRemoveFromProject={() => handleRemoveFromProject(contextMenu.conversation.id)}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Notification Toast */}
      {notification && (
        <div
          style={{
            position: 'fixed', bottom: 24, right: 24, padding: '12px 16px',
            background: 'var(--panel)', border: '1px solid var(--rule)', color: 'var(--fg)',
            borderRadius: 3, boxShadow: 'var(--shadow)', zIndex: 110,
            fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.04em'
          }}
        >
          {notification}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
